<template>
  <v-col :cols="isMobile ? '12' : '8'" class="card-col">
    <v-card
      class="full-width"
      :class="cardClasses"
      :style="cardStyle"
      :color="cardColor"
      :elevation="cardElevation"
    >
      <!-- Programs / Library View -->
      <ProgramsDataTable
        v-if="currentView === 'programs' || currentView === 'library'"
        :key="currentView"
        :search="search"
        :show-expand="currentView === 'programs'"
        :show-program-status="currentView === 'programs'"
        :selected="selectedPrograms"
        :expanded-programs="expandedPrograms"
        :source-page="sourcePage"
        :table="programTableOptions"
        :budgets="budgets"
        @expand-program="handleExpandProgram"
        @collapse-program="handleCollapseProgram"
        @select-item="handleProgramSelect"
        @preview-item="handleProgramPreview"
        @message-program-audience="handleMessageProgramAudience"
        @load-program-wizard="handleLoadProgramWizard"
        @load-metric-ui="handleLoadMetricUI"
        @get-budget="handleGetBudget"
        @delete-program-confirmation="handleProgramDeleteConfirmation"
      >
      </ProgramsDataTable>

      <!-- Forms View -->
      <FormsDataTable
        v-else-if="currentView === 'forms'"
        :search="search"
        :admin-view="true"
        :selected="selectedForms"
        :source-page="sourcePage"
        :source-page-props="{
          preload: 'forms',
        }"
        @select-item="handleFormSelect"
        @preview-item="handleFormPreview"
      />

      <!-- Rewards View -->
      <RewardsLibrary v-else-if="currentView === 'rewards'" @load-action="handleRewardsAction" />

      <!-- Learning View -->
      <LearningLibrary
        v-else-if="currentView === 'learning'"
        :search="search"
        :display-surveys="false"
        :source-page="sourcePage"
        :source-page-props="{
          preload: 'learning',
        }"
        @item-select="handleLearningSelect"
        @item-preview="handleLearningPreview"
      />

      <!-- Surveys View -->
      <LearningLibrary
        v-else-if="currentView === 'surveys'"
        :search="search"
        :display-surveys="true"
        :source-page="sourcePage"
        :source-page-props="{
          preload: 'surveys',
        }"
        @item-select="handleSurveySelect"
        @item-preview="handleSurveyPreview"
      />

      <!-- Claims Configuration View -->
      <ClaimAuditRules v-else-if="currentView === 'claims-config'" />

      <div
        v-else
        class="d-flex flex-column align-center justify-center pa-8"
        style="min-height: 300px"
      >
        <v-icon size="64" color="grey lighten-1">mdi-folder-open</v-icon>
        <h3 class="grey--text mt-4">Select a view from the sidebar</h3>
      </div>
    </v-card>
  </v-col>
</template>

<script>
import ProgramsDataTable from "@/components/program/admin/ProgramAdminV3/AdminDataTable";
import ClaimAuditRules from "@/components/program/admin/ProgramAdminV3/ClaimsManagement/ClaimAuditRules.vue";
import FormsDataTable from "@/components/data-table/Forms.vue";
import LearningLibrary from "@/components/program/admin/LearningLibrary.vue";
import RewardsLibrary from "@/components/rewards-admin/RewardsLibrary.vue";

export default {
  name: "AdminContentArea",
  components: {
    ProgramsDataTable,
    FormsDataTable,
    LearningLibrary,
    RewardsLibrary,
    ClaimAuditRules,
  },
  props: {
    currentView: {
      type: [String, Number],
      required: true,
    },
    search: {
      type: String,
      default: "",
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    sourcePage: {
      type: String,
      default: "program-admin",
    },
    programTableOptions: {
      type: Object,
      default: () => ({}),
    },
    budgets: {
      type: Object,
      default: () => ({}),
    },
    // Selected items for different views
    selectedPrograms: {
      type: Array,
      default: () => [],
    },
    expandedPrograms: {
      type: Array,
      default: () => [],
    },
    selectedForms: {
      type: Array,
      default: () => [],
    },
    selectedLibraryItems: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    cardClasses() {
      const classes = ["mx-0"];

      if (this.currentView === "rewards") {
        classes.push("pa-0");
      } else {
        classes.push("pa-4");
      }

      return classes;
    },
    cardStyle() {
      const style = {
        "border-radius": "10px",
      };
      if (this.currentView === "rewards") {
        style["min-height"] = "100%";
      }
      return style;
    },
    cardColor() {
      return this.currentView === "rewards" ? "transparent" : "white";
    },
    cardElevation() {
      return this.currentView === "rewards" ? 0 : 1;
    },
  },
  methods: {
    // Program handlers
    handleExpandProgram(item) {
      this.$emit("expand-program", item);
    },
    handleCollapseProgram(item) {
      this.$emit("collapse-program", item);
    },
    handleProgramSelect(item) {
      this.$emit("program-select", item);
    },
    handleProgramPreview(item) {
      this.$emit("program-preview", item);
    },
    handleProgramDeleteConfirmation(item) {
      this.$emit("program-delete-confirmation", item);
    },
    handleMessageProgramAudience(item, type) {
      this.$emit("message-program-audience", item, type);
    },
    handleLoadProgramWizard(item, editing, preloadedStep) {
      this.$emit("load-program-wizard", item, editing, preloadedStep);
    },
    handleLoadMetricUI(item) {
      this.$emit("load-metric-ui", item);
    },
    handleGetBudget(item) {
      this.$emit("get-budget", item);
    },

    // Form handlers
    handleFormSelect(item) {
      this.$emit("form-select", item);
    },
    handleFormPreview(item) {
      this.$emit("form-preview", item);
    },

    // Learning handlers
    handleLearningSelect(item) {
      this.$emit("learning-select", item);
    },
    handleLearningPreview(item) {
      this.$emit("learning-preview", item);
    },

    // Survey handlers
    handleSurveySelect(item) {
      this.$emit("survey-select", item);
    },
    handleSurveyPreview(item) {
      this.$emit("survey-preview", item);
    },

    // Library handlers
    handleLibrarySelect(item) {
      this.$emit("library-select", item);
    },
    handleLibraryPreview(item) {
      this.$emit("library-preview", item);
    },

    // Rewards handlers
    handleRewardsAction(action) {
      this.$emit("rewards-action", action);
    },
  },
};
</script>
